import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Award, Check, PlayCircle } from 'lucide-react';
import type { PublicCourse } from '@mlm/shared';
import { isNativeShell } from '@mlm/ui/lib/native-shell';
import { Block, CoachButton, ErrorNote, Loading, Screen } from '../components/primitives';
import { CoursePurchase } from '../components/course-purchase';
import { COACH_URL, fmtPeso, learn, store } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { courseAction, courseWebUrl, visitorAction } from '../lib/course-access';
import { qk, usePublicConfig } from '../lib/queries';

/**
 * One course, by its PRODUCT id (the catalogue's key). The description and
 * outline are public; the box at the top is decided by `courseAction` from the
 * member's own access (or `visitorAction` for someone not signed in), so the
 * web and the phone app say different things in one place.
 */
export function CourseDetailPage() {
  const { productId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const native = isNativeShell();
  const signedIn = !!useAuthStore((s) => s.token);
  const config = usePublicConfig();

  const course = useQuery({
    queryKey: qk.coursePage(productId),
    queryFn: () => learn.course(productId),
    enabled: !!productId,
  });
  const courseId = course.data?.id ?? '';
  const player = useQuery({
    queryKey: qk.coursePlayer(courseId),
    queryFn: () => learn.player(courseId),
    enabled: signedIn && !!courseId,
    retry: 0,
  });
  const ctx = useQuery({
    queryKey: qk.shopContext,
    queryFn: store.paymentMethods,
    staleTime: 5 * 60_000,
    enabled: signedIn,
  });
  const product = useQuery({
    queryKey: qk.shopProduct(productId),
    queryFn: () => store.product(productId, ctx.data?.slotId ?? null),
    // Only the web sells, so only the web needs the price.
    enabled:
      signedIn &&
      !native &&
      !!productId &&
      !ctx.isPending &&
      course.data?.accessLevel === 'purchase',
  });
  // A visitor on the web is shown the public price of a paid course.
  const publicProduct = useQuery({
    queryKey: [...qk.publicFeed, productId],
    queryFn: () => learn.publicProduct(productId),
    enabled: !signedIn && !native && !!productId && course.data?.accessLevel === 'purchase',
    retry: 0,
  });

  const enroll = useMutation({
    mutationFn: () => learn.enroll(courseId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.myCourses });
      navigate(`/learn/${courseId}`);
    },
  });

  if (course.isPending) return <Loading label="Loading the course" />;
  if (course.error) {
    return (
      <Screen eyebrow="Learn" title="Course" back="/learn/browse">
        <ErrorNote error={course.error} retry={() => void course.refetch()} />
      </Screen>
    );
  }
  const c = course.data;
  const webLink = config.data?.lmsAppWebLink === true;
  const action = courseAction({
    course: c,
    access: player.data?.access ?? null,
    native,
    webLink,
  });
  const lessons = c.chapters.reduce((n, ch) => n + ch.lessons.length, 0);

  return (
    <Screen
      eyebrow={c.categoryName ?? 'Course'}
      title={c.name}
      subtitle={c.subtitle ?? undefined}
      back="/learn/browse"
      wide
    >
      <section className="border-b border-border px-5 py-6">
        <p className="text-[0.88rem] text-sage-deep">
          {[
            c.creatorName && !c.creatorIsHouse ? `By ${c.creatorName}` : null,
            lessons === 1 ? '1 lesson' : `${lessons} lessons`,
            c.level,
            c.ratingCount > 0 && c.ratingAvg ? `${Number(c.ratingAvg).toFixed(1)} of 5` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>

        <div className="mt-5">
          {!signedIn ? (
            <VisitorBox
              course={c}
              native={native}
              action={visitorAction({
                course: c,
                native,
                webLink,
                signupMode: config.data?.signupMode ?? 'on',
                joinAllowed: config.data?.campaignPageSignupAllowed === true,
              })}
              price={publicProduct.data?.price ?? null}
            />
          ) : player.isPending && !!courseId ? (
            <p className="eyebrow">Checking your access…</p>
          ) : action.kind === 'continue' ? (
            <CoachButton kind="gold" block onClick={() => navigate(`/learn/${c.id}`)}>
              Continue learning
            </CoachButton>
          ) : action.kind === 'enroll' ? (
            <>
              {enroll.error != null && <ErrorNote error={enroll.error} />}
              <CoachButton
                kind="gold"
                block
                disabled={enroll.isPending}
                onClick={() => enroll.mutate()}
              >
                {enroll.isPending
                  ? 'Opening…'
                  : c.accessLevel === 'free'
                    ? 'Start, it is free'
                    : 'Start learning'}
              </CoachButton>
            </>
          ) : action.kind === 'buy' ? (
            product.isPending ? (
              <p className="eyebrow">Loading the price…</p>
            ) : product.data ? (
              <CoursePurchase
                mode="buy"
                seller={{ creatorUserId: c.creatorUserId, isHouse: c.creatorIsHouse }}
                options={[
                  {
                    productId: c.productId,
                    label: c.name,
                    price: product.data.product.priceEffective,
                  },
                ]}
              />
            ) : (
              <ErrorNote error={product.error} retry={() => void product.refetch()} />
            )
          ) : action.kind === 'subscribe' ? (
            <>
              <p className="mb-5 text-[0.93rem] leading-relaxed text-ink/70">
                This course comes with a subscription
                {c.creatorName && !c.creatorIsHouse ? ` to ${c.creatorName}` : ''}, which opens
                every subscribers-only course {c.creatorIsHouse ? 'here' : 'they publish'}.
              </p>
              <CoursePurchase
                mode="subscribe"
                seller={{ creatorUserId: c.creatorUserId, isHouse: c.creatorIsHouse }}
                options={action.plans.map((p) => ({
                  productId: p.productId,
                  label: p.label,
                  price: p.price,
                  period: p.period,
                }))}
              />
            </>
          ) : action.kind === 'outside_app' ? (
            <p className="border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.93rem] leading-relaxed text-ink/85">
              This course is not part of your access yet. Courses are managed outside the app; once
              you have this one, it opens here on its own.
            </p>
          ) : action.kind === 'on_web' ? (
            <WebLink
              productId={c.productId}
              label="Continue on the website"
              note="Opens this course on our website, where you can get it. Log in there with the same account; once it is yours, it opens here on its own."
            />
          ) : (
            <p className="text-[0.93rem] text-ink/70">
              This course is not open to new learners right now. Please check back soon.
            </p>
          )}
        </div>
      </section>

      {c.learnPoints.length > 0 && (
        <Block eyebrow="What you will learn">
          <ul className="mt-2 space-y-2">
            {c.learnPoints.map((point) => (
              <li key={point} className="flex items-start gap-3">
                <Check className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </Block>
      )}

      <Block eyebrow="Inside the course">
        <div className="mt-2 space-y-5">
          {c.chapters.map((ch) => (
            <div key={ch.id}>
              <p className="text-[0.95rem] text-ink">{ch.title}</p>
              <ul className="mt-2 space-y-1.5">
                {ch.lessons.map((l) => (
                  <li key={l.id} className="flex items-start gap-2 text-[0.88rem] text-ink/70">
                    <PlayCircle className="mt-0.5 h-4 w-4 shrink-0 text-sage-deep" aria-hidden />
                    <span className="min-w-0">{l.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Block>

      {c.requirements.length > 0 && (
        <Block eyebrow="Before you start">
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {c.requirements.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </Block>
      )}

      {c.hasCertificateTemplate && (
        <section className="flex items-center gap-3 border-b border-border px-5 py-5 text-[0.9rem] text-ink/75">
          <Award className="h-5 w-5 shrink-0 text-gold" aria-hidden />
          Finish the course to earn a certificate.
        </section>
      )}
    </Screen>
  );
}

/**
 * The phone app's link out to the same course on the website (`lmsAppWebLink`).
 * A plain link to another host: the app shell hands it to the phone's browser
 * rather than loading the website inside the app.
 */
function WebLink({ productId, label, note }: { productId: string; label: string; note: string }) {
  return (
    <div className="space-y-3">
      <a href={courseWebUrl(COACH_URL, productId)} className="block">
        <CoachButton kind="gold" block>
          {label}
        </CoachButton>
      </a>
      <p className="text-[0.88rem] leading-relaxed text-ink/70">{note}</p>
    </div>
  );
}

/** What a visitor who is not signed in is offered (see `visitorAction`). */
function VisitorBox({
  course,
  native,
  action,
  price,
}: {
  course: PublicCourse;
  native: boolean;
  action: ReturnType<typeof visitorAction>;
  /** The public price of a paid course (web only), once loaded. */
  price: string | null;
}) {
  const here = `/learn/c/${course.productId}`;
  const login = (
    <p className="text-[0.88rem] text-ink/70">
      Already a member?{' '}
      <Link
        to={`/login?next=${encodeURIComponent(here)}`}
        className="text-ink underline underline-offset-4"
      >
        Log in
      </Link>
    </p>
  );
  if (action.kind === 'join') {
    return (
      <div className="space-y-3">
        {!native && <PriceLine course={course} price={price} />}
        <Link to={`/signup?join=${encodeURIComponent(course.productId)}`} className="block">
          <CoachButton kind="gold" block>
            Join Miracles Protocol
          </CoachButton>
        </Link>
        {login}
      </div>
    );
  }
  if (action.kind === 'join_on_web') {
    return (
      <div className="space-y-3">
        <WebLink
          productId={course.productId}
          label="Join Miracles Protocol"
          note="Opens this course on our website, where you can create your account and get it. Then log in here with the same account and it opens on its own."
        />
        {login}
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <p className="border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.93rem] leading-relaxed text-ink/85">
        {action.kind === 'invite_only'
          ? 'Miracles Protocol is joined by invitation. Open the link you were sent to create your account.'
          : 'Joining Miracles Protocol happens outside the app. Once you have an account, log in here and your courses open on their own.'}
      </p>
      {login}
    </div>
  );
}

/** A visitor's price on the web: the plans of a subscription, or a course's one price. */
function PriceLine({ course, price }: { course: PublicCourse; price: string | null }) {
  if (course.accessLevel === 'free') {
    return <p className="font-display text-[1.2rem] font-semibold text-ink">Free</p>;
  }
  if (course.accessLevel === 'subscriber') {
    const plans = course.subscription?.plans ?? [];
    if (plans.length === 0) return null;
    return (
      <div>
        <p className="text-[0.93rem] text-ink/70">Included in a subscription:</p>
        <ul className="mt-1 space-y-0.5">
          {plans.map((p) => (
            <li key={p.productId} className="text-[0.95rem] text-ink">
              {p.label}, {fmtPeso(p.price)}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return price ? (
    <p className="font-display text-[1.2rem] font-semibold text-ink">{fmtPeso(price)}</p>
  ) : null;
}
